// components/InvoiceQuickView.jsx
import React, { useEffect, useRef, useState } from "react";
import { X, Printer } from "lucide-react";
import { useReactToPrint } from "react-to-print";
import InnerLoader from "../ui/loader/inner-loader";
import { useApiCall } from "../custom-hooks/api-call";

const InvoiceQuickView = ({
  onClose,
  data,
  type,
  invoiceLoading,
  projectkey="",
}) => {
  // console.log("data", data)

  const printRef = useRef(null);

  //   const brandMeta = data?.brand_details?.meta || [];
  const [invoiceData, setInvoiceData] = useState(data || {});
  const [loading, setLoading] = useState(projectkey ? true : false);
  const [brandMeta, setBrandMeta] = useState(data?.brand_details?.meta || []);
  const apiCall = useApiCall();

  const getData = async (projectkey) => {
    setLoading(true);
    try {
      const res = await apiCall({
        method: "get",
        url: `transactions/get-invoice/${projectkey}`,
      });
      setBrandMeta(res?.data?.brand_details?.meta);
      setInvoiceData(res?.data);
    } catch (error) {
      console.warn("Error : ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectkey) {
      getData(projectkey);
    }
  }, []);

  // find each meta value by key
  const brandAddress = brandMeta.find(
    (m) => m?.meta_key === "address",
  )?.meta_value;
  const brandPhone = brandMeta.find((m) => m?.meta_key === "phone")?.meta_value;
  const brandEmail = brandMeta.find((m) => m?.meta_key === "email")?.meta_value;
  const brandDesc = brandMeta.find(
    (m) => m?.meta_key === "invoice_brand_description",
  )?.meta_value;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: data?.invoice_number,
    copyStyles: true,
    pageStyle: `
      @page { size: A4; margin: 20mm; }
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        background: white !important;
      }
      * { box-sizing: border-box; }
    `,
  });

  return (
    <div className="fixed inset-0  !pr-0  bg-black/50 backdrop-blur-sm flex justify-end z-[9999] !mt-0">
      <div className="w-[740px] h-full bg-gradient-to-b  from-white to-gray-50 shadow-2xl rounded-l-2xl overflow-y-auto animate-slide-left">
        {/* !invoiceLoading || */}
        { !loading ? (
          <>
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b bg-white sticky top-0 z-10 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-800">
                {invoiceData?.invoice_id}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </button>
                <button
                  onClick={onClose}
                  className="p-2 rounded-md hover:bg-gray-100 transition"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Printable area */}
            <div ref={printRef} className="p-6 space-y-8">
              {/* Brand Header */}
              <div className="flex justify-between items-center border rounded-lg p-5 bg-white shadow-sm">
                <div className="flex items-center gap-4">
                  <img
                    src={invoiceData?.brand_details?.logo_base64}
                    alt="Logo"
                    className="h-24 w-32 object-contain"
                  />
                  {/* <a href={invoiceData?.brand_details?.brandurl} target="_blank" rel="noopener noreferrer">
                                        <h3 className="text-xl font-semibold text-gray-800">
                                            {invoiceData?.brand_details?.brandname}
                                        </h3>
                                    </a> */}
                </div>
                <div className="text-right text-sm text-gray-600 leading-5">
                  <a
                    href={invoiceData?.brand_details?.brandurl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <h3 className="text-[18px] font-semibold text-gray-800">
                      {invoiceData?.brand_details?.brandname}
                    </h3>
                  </a>
                  <p>{brandAddress || "-"}</p>
                  <p>{brandPhone || "-"}</p>
                  <p>{brandEmail || "-"}</p>
                </div>
              </div>

              {/* Invoice Info */}
              <div className="flex justify-between items-start gap-6 border-b pb-4 bg-gray-100 p-5">
                <div className="w-1/2">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Invoice Details
                  </h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>
                      <strong>Invoice #:</strong> {invoiceData?.invoice_id}
                    </p>
                    <p>
                      <strong>
                        {type == "project" ? "Created:" : "Issue Date:"}
                      </strong>{" "}
                      {type == "project"
                        ? invoiceData?.deal_details?.dealtime
                        : invoiceData?.issue_date}
                    </p>
                    <p>
                      <strong>
                        {type == "project" ? "Paid:" : "Due Date:"}
                      </strong>{" "}
                      {type == "project"
                        ? invoiceData?.invoice_date
                        : invoiceData?.due_date}
                    </p>
                  </div>
                </div>
                <div className="w-1/2 text-end">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Billed To
                  </h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>{invoiceData?.client_details?.name}</p>
                    <p>{invoiceData?.client_details?.email}</p>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">
                  Item Details
                </h4>
                <p className="text-xs text-gray-400 mb-6">
                  BELOW ARE THE PAYMENT DETAILS
                </p>
                <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="py-2 px-5 text-left font-bold">#</th>
                        <th className="py-2 px-5 text-left font-bold">
                          Product
                        </th>
                        <th className="py-2 px-5 text-right font-bold">Rate</th>
                        <th className="py-2 px-5 text-right font-bold">
                          Discount
                        </th>
                        <th className="py-2 px-5 text-right font-bold">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t hover:bg-gray-50">
                        <td className="py-3 px-5">1</td>
                        <td className="py-3 px-5">
                          {invoiceData?.invoice_summary?.deal_type}
                        </td>
                        <td className="py-3 px-5 text-right">
                          {invoiceData?.invoice_summary?.amount}
                        </td>
                        <td className="py-3 px-5 text-right">
                          {invoiceData?.invoice_summary?.discount}
                        </td>
                        <td className="py-3 px-5 text-right">
                          {invoiceData?.invoice_summary?.amount}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-between items-start border-t pt-6 text-gray-700 text-sm">
                <div className="text-xs text-gray-400 w-56 leading-relaxed">
                  *THIS IS A SYSTEM GENERATED DOCUMENT POWERED BY{" "}
                  <span className="text-black font-medium">OPRAVIQ</span>*
                </div>
                <div className="space-y-1 text-right">
                  <div className="flex justify-between w-52 ml-auto">
                    <span>Subtotal:</span>
                    <span>{invoiceData?.invoice_summary?.amount}</span>
                  </div>
                  <div className="flex justify-between w-52 ml-auto">
                    <span>Amount Paid:</span>
                    <span>{invoiceData?.invoice_summary?.total_amount}</span>
                  </div>
                  <div className="flex justify-between w-52 ml-auto">
                    <span>Balance:</span>
                    <span>{invoiceData?.invoice_summary?.balance}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg text-black border-t pt-2 w-52 ml-auto">
                    <span>Total:</span>
                    <span>{invoiceData?.invoice_summary?.total_amount}</span>
                  </div>
                </div>
              </div>

              {brandDesc && (
                <div className="mt-10 pt-10 border-t text-sm text-gray-600 leading-relaxed">
                  <p className="whitespace-pre-line">{brandDesc}</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <InnerLoader />
        )}
      </div>
    </div>
  );
};

export default InvoiceQuickView;
